"use client";

import { useEffect, useMemo, useRef } from "react";
import type {
  CheckoutOptions,
  PaymentCheckout,
  ResumeCheckoutOptions,
} from "./types";

async function loadPopup(): Promise<unknown> {
  try {
    // biome-ignore lint/suspicious/noExplicitAny: @paystack/inline-js ships no type declarations
    const mod: any = await import("@paystack/inline-js" as any);
    const PaystackCtor = mod.default ?? mod;
    return new PaystackCtor();
  } catch {
    return null;
  }
}

export function usePaystackCheckout(publicKey: string): PaymentCheckout {
  const queueRef = useRef<Promise<unknown>[]>([]);

  useEffect(() => {
    // Pre-create two instances in parallel at mount — one for the first
    // click, one for a quick cancel-and-reselect — so both opens get a
    // fully-loaded iframe without waiting on CDN at click time.
    queueRef.current = [loadPopup(), loadPopup()];
  }, []);

  return useMemo<PaymentCheckout>(() => {
    // Dequeue the oldest pre-warmed instance and immediately enqueue a fresh
    // replacement so the queue is never empty.
    const takePopup = (): Promise<unknown> => {
      const queue = queueRef.current;
      const popupPromise = queue.shift() ?? loadPopup();
      queue.push(loadPopup());
      return popupPromise;
    };

    return {
      open(options: CheckoutOptions) {
        takePopup()
          .then((popup) => {
            if (!popup) {
              options.onError("Failed to load payment processor");
              return;
            }
            // biome-ignore lint/suspicious/noExplicitAny: @paystack/inline-js ships no type declarations
            (popup as any).newTransaction({
              key: publicKey,
              email: options.email,
              amount: options.planCode ? 0 : (options.amount ?? 0),
              ...(options.planCode ? { planCode: options.planCode } : {}),
              metadata: options.metadata ?? {},
              onSuccess: (tx: { reference: string }) =>
                options.onSuccess(tx.reference),
              onCancel: () => options.onCancel(),
              onError: (err: { message: string }) =>
                options.onError(err.message),
            });
          })
          .catch(() => options.onError("Failed to load payment processor"));
      },
      resume(options: ResumeCheckoutOptions) {
        takePopup()
          .then((popup) => {
            if (!popup) {
              options.onError("Failed to load payment processor");
              return;
            }
            // biome-ignore lint/suspicious/noExplicitAny: @paystack/inline-js ships no type declarations
            (popup as any).resumeTransaction(options.accessCode, {
              onSuccess: (tx: { reference: string }) =>
                options.onSuccess(tx.reference),
              onCancel: () => options.onCancel(),
              onError: (err: { message: string }) =>
                options.onError(err.message),
            });
          })
          .catch(() => options.onError("Failed to load payment processor"));
      },
    };
  }, [publicKey]);
}
